import type { PortalHubConnection } from '../realtime/hubFactory';
import type { CallId, Incident, IncidentId, PortalCall } from '../realtime/contracts';
import {
  normalizeIncident,
  normalizePortalCall,
  normalizePortalOverview,
} from '../realtime/contracts';
import { IncidentStore } from './incidentStore';
import { CallStore } from './callStore';
import { ConnectionStateTracker } from './connectionStateTracker';

export type PortalClientOptions = {
  hub: PortalHubConnection;
  incidents?: IncidentStore;
  calls?: CallStore;
  onError?: (message: string, error: unknown) => void;
};

export const EVICT_EXPIRED_EVERY_MS = 60_000;
export const STALE_CONNECTION_THRESHOLD_MS = 30_000;
export const ENSURE_ALIVE_RELOAD_THROTTLE_MS = 5_000;

const START_RETRY_DELAYS_MS = [2_000, 5_000, 10_000] as const;

type PushedDuringReload = {
  incidents: Set<IncidentId>;
  calls: Set<CallId>;
};

export class PortalClient {
  public readonly incidents: IncidentStore;
  public readonly calls: CallStore;
  public readonly connection: ConnectionStateTracker;

  private readonly hub: PortalHubConnection;
  private readonly reportError: (message: string, error: unknown) => void;
  private readonly detachHandlers: (() => void)[] = [];
  private readonly evictionTimer: ReturnType<typeof setInterval>;

  private readonly retainedIncidents = new Map<IncidentId, number>();
  private pushedDuringReload: PushedDuringReload | null = null;
  private reloadInFlight: Promise<void> | null = null;
  private lastReloadFinishedAt: number | null = null;
  private ensureAliveInFlight: Promise<void> | null = null;
  private startRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private startRetryAttempt = 0;
  private startGeneration = 0;
  private recentIncidentsWindowSeconds: number | null = null;
  private recentCallsWindowSeconds: number | null = null;
  private disposed = false;

  public get hubConnection(): PortalHubConnection {
    return this.hub;
  }

  public constructor(options: PortalClientOptions) {
    this.hub = options.hub;
    this.incidents = options.incidents ?? new IncidentStore();
    this.calls = options.calls ?? new CallStore();
    this.connection = new ConnectionStateTracker();
    this.reportError = options.onError ?? (() => undefined);

    this.detachHandlers.push(
      this.hub.on('IncidentChanged', (incident) => {
        const normalized = normalizeIncident(incident);
        this.pushedDuringReload?.incidents.add(normalized.id);
        this.incidents.upsert(normalized);
      }),
      this.hub.on('CallChanged', (call) => {
        const normalized = normalizePortalCall(call);
        this.pushedDuringReload?.calls.add(normalized.id);
        this.calls.upsert(normalized);
        this.incidents.updateCall(this.calls.getById(normalized.id)!);
        this.dropCallsOutsideTheRecentWindow();
      }),
      this.hub.on('IncidentCallChanged', (call) => {
        this.incidents.updateCall(normalizePortalCall(call));
      }),
      this.hub.onReconnecting(() => this.connection.markDisconnected()),
      this.hub.onReconnected(() => this.resume()),
      this.hub.onClose(() => {
        if (!this.disposed) {
          this.connection.markDisconnected();
          this.scheduleStartRetry();
        }
      }),
    );

    this.evictionTimer = setInterval(() => this.evictExpired(), EVICT_EXPIRED_EVERY_MS);
    this.connect();
  }

  public connect(): void {
    this.cancelStartRetry();
    this.startRetryAttempt = 0;
    this.startHub();
  }

  private startHub(): void {
    if (this.disposed || !this.hub.isDisconnected()) {
      return;
    }

    const attempt = ++this.startGeneration;
    void this.hub.start().then(
      () => {
        if (!this.disposed && attempt === this.startGeneration) {
          this.cancelStartRetry();
          this.startRetryAttempt = 0;
          this.resume();
        }
      },
      (error: unknown) => {
        if (this.disposed || attempt !== this.startGeneration) {
          return;
        }

        this.reportError('Connecting to live updates failed.', error);
        this.connection.markDisconnected();
        this.scheduleStartRetry();
      },
    );
  }

  private scheduleStartRetry(): void {
    this.cancelStartRetry();
    const delay = START_RETRY_DELAYS_MS[this.startRetryAttempt] ?? 10_000;
    this.startRetryAttempt += 1;
    this.startRetryTimer = setTimeout(() => {
      this.startRetryTimer = null;
      this.startHub();
    }, delay);
  }

  private cancelStartRetry(): void {
    if (this.startRetryTimer !== null) {
      clearTimeout(this.startRetryTimer);
      this.startRetryTimer = null;
    }
  }

  public ensureAlive(suspendedForMs: number | null): Promise<void> {
    this.ensureAliveInFlight ??= this.reviveConnection(suspendedForMs).finally(() => {
      this.ensureAliveInFlight = null;
    });

    return this.ensureAliveInFlight;
  }

  private async reviveConnection(suspendedForMs: number | null): Promise<void> {
    if (this.disposed) {
      return;
    }

    try {
      if (this.hub.isDisconnected()) {
        this.connect();
        return;
      }

      if (this.hub.isConnecting()) {
        return;
      }

      if (suspendedForMs !== null && suspendedForMs > STALE_CONNECTION_THRESHOLD_MS) {
        this.startGeneration += 1;
        await this.hub.stop();
        this.connect();
        return;
      }

      if (this.reloadInFlight === null && !this.reloadedRecently()) {
        await this.reload();
      }
    } catch (error) {
      if (!this.disposed) {
        this.reportError('Restoring the live updates failed.', error);
      }
    }
  }

  private reloadedRecently(): boolean {
    return (
      this.lastReloadFinishedAt !== null &&
      Date.now() - this.lastReloadFinishedAt < ENSURE_ALIVE_RELOAD_THROTTLE_MS
    );
  }

  public async reload(): Promise<void> {
    try {
      await this.reloadOrThrow();
    } catch (error) {
      this.reportError('Loading the live incidents failed.', error);
      if (!this.disposed) {
        this.incidents.markFailed();
      }
    }
  }

  private reloadOrThrow(): Promise<void> {
    this.reloadInFlight ??= this.readOverview().finally(() => {
      this.reloadInFlight = null;
      this.lastReloadFinishedAt = Date.now();
    });

    return this.reloadInFlight;
  }

  private async readOverview(): Promise<void> {
    const pushed: PushedDuringReload = { incidents: new Set(), calls: new Set() };
    this.pushedDuringReload = pushed;

    try {
      const overview = normalizePortalOverview(await this.hub.invoke('GetOverview'));
      if (this.disposed) {
        return;
      }

      this.recentIncidentsWindowSeconds = overview.recentIncidentsWindowSeconds;
      this.recentCallsWindowSeconds = overview.recentCallsWindowSeconds;

      const inOverview = new Set(overview.incidents.map((incident) => incident.id));
      const pushedMeanwhile = this.knownIncidents(
        [...pushed.incidents].filter((incidentId) => !inOverview.has(incidentId)),
      );
      const keptForOpenDetails = this.knownIncidents(
        [...this.retainedIncidents.keys()].filter(
          (incidentId) => !inOverview.has(incidentId) && !pushed.incidents.has(incidentId),
        ),
      );

      const recentOverviewCalls = this.callsInsideTheRecentWindow(overview.calls);
      const callsInOverview = new Set(recentOverviewCalls.map((call) => call.id));
      const keepCalls = [...pushed.calls]
        .filter((callId) => !callsInOverview.has(callId))
        .map((callId) => this.calls.getById(callId))
        .filter(
          (call): call is PortalCall =>
            call !== null &&
            !this.endedOutsideWindow(call.endTimeUtc, this.recentCallsWindowSeconds),
        );

      this.incidents.replaceAll([...overview.incidents, ...pushedMeanwhile], keptForOpenDetails);
      this.calls.replaceAll(recentOverviewCalls, keepCalls);
    } finally {
      if (this.pushedDuringReload === pushed) {
        this.pushedDuringReload = null;
      }
    }
  }

  public async loadIncident(incidentId: IncidentId): Promise<Incident | null> {
    this.incidents.forgetReadFailure(incidentId);

    try {
      const incidentWire = await this.hub.invoke('GetIncident', incidentId);
      if (this.disposed) {
        return null;
      }

      if (incidentWire === null) {
        this.incidents.markNotFound(incidentId);
        return null;
      }

      const incident = normalizeIncident(incidentWire);
      this.incidents.keepOutsideTheLiveWindow(incident);

      return this.incidents.getById(incidentId);
    } catch (error) {
      this.reportError(`Loading the incident ${incidentId} failed.`, error);
      if (!this.disposed) {
        this.incidents.markReadFailed(incidentId);
      }
      return null;
    }
  }

  public retainIncident(incidentId: IncidentId): void {
    this.retainedIncidents.set(incidentId, (this.retainedIncidents.get(incidentId) ?? 0) + 1);
  }

  public releaseIncident(incidentId: IncidentId): void {
    const count = this.retainedIncidents.get(incidentId);
    if (count === undefined) {
      return;
    }

    if (count <= 1) {
      this.retainedIncidents.delete(incidentId);
    } else {
      this.retainedIncidents.set(incidentId, count - 1);
    }
  }

  public dispose(): void {
    this.disposed = true;
    this.cancelStartRetry();
    clearInterval(this.evictionTimer);
    this.detachHandlers.forEach((detach) => detach());
    this.connection.dispose();
    void this.hub.dispose();
  }

  private resume(): void {
    this.connection.markConnected();
    void this.reload();
  }

  private evictExpired(): void {
    this.dropIncidentsNothingNeeds();
    this.dropCallsOutsideTheRecentWindow();
  }

  private dropIncidentsNothingNeeds(): void {
    const { liveIncidents, incidentsById } = this.incidents.getSnapshot();
    const liveIds = new Set(liveIncidents.map((incident) => incident.id));

    this.incidents.drop(
      [...incidentsById.values()]
        .filter(
          (incident) =>
            !this.retainedIncidents.has(incident.id) &&
            (!liveIds.has(incident.id) ||
              this.endedOutsideWindow(incident.closedTimeUtc, this.recentIncidentsWindowSeconds)),
        )
        .map((incident) => incident.id),
    );
  }

  private dropCallsOutsideTheRecentWindow(): void {
    this.calls.drop(
      this.callsOutsideTheRecentWindow(this.calls.getSnapshot().calls).map((call) => call.id),
    );
  }

  private callsInsideTheRecentWindow(calls: readonly PortalCall[]): PortalCall[] {
    return calls.filter(
      (call) => !this.endedOutsideWindow(call.endTimeUtc, this.recentCallsWindowSeconds),
    );
  }

  private callsOutsideTheRecentWindow(calls: readonly PortalCall[]): PortalCall[] {
    return calls.filter((call) =>
      this.endedOutsideWindow(call.endTimeUtc, this.recentCallsWindowSeconds),
    );
  }

  private endedOutsideWindow(endedTimeUtc: string | null, windowSeconds: number | null): boolean {
    if (windowSeconds === null || endedTimeUtc === null) {
      return false;
    }

    return Date.parse(endedTimeUtc) <= Date.now() - windowSeconds * 1_000;
  }

  private knownIncidents(incidentIds: readonly IncidentId[]): Incident[] {
    return incidentIds
      .map((incidentId) => this.incidents.getById(incidentId))
      .filter((incident) => incident !== null);
  }
}
